/**
 * Display-only evidence for /password-changed copy selection.
 *
 * The Auth0 Password Reset / Post Challenge Action appends
 *   ?kk_result=password_changed&kk_ts=<unix seconds>
 * to the result URL. Auth0 also sends browsers to /password-changed (the
 * Application Login URI) for unrelated fallbacks — stale Universal Login
 * state, Back to a cached login page, error-page "Back to application" —
 * usually with ?iss=. Those must not claim a password change.
 *
 * This marker is NOT proof: anyone can type it. It only chooses truthful
 * success vs neutral copy. Never use it to authorize, identify, or change
 * session state — session invalidation must run on every arrival.
 */

export const PASSWORD_CHANGED_RESULT_PARAM = "kk_result";
export const PASSWORD_CHANGED_RESULT_VALUE = "password_changed";
export const PASSWORD_CHANGED_TS_PARAM = "kk_ts";

/** Post Challenge stamps kk_ts before the user types the new password. */
export const PASSWORD_CHANGED_MAX_AGE_SEC = 30 * 60;
export const PASSWORD_CHANGED_MAX_FUTURE_SKEW_SEC = 5 * 60;

export const PASSWORD_CHANGED_MODE_SUCCESS = "success";
export const PASSWORD_CHANGED_MODE_NEUTRAL = "neutral";

/**
 * Pure: "success" only for the exact marker with a fresh, sane timestamp;
 * "neutral" for everything else.
 */
export function resolvePasswordChangedMode(search, nowMs = Date.now()) {
  let params;
  try {
    params = new URLSearchParams(typeof search === "string" ? search : "");
  } catch {
    return PASSWORD_CHANGED_MODE_NEUTRAL;
  }

  if (params.getAll(PASSWORD_CHANGED_RESULT_PARAM).length !== 1) {
    return PASSWORD_CHANGED_MODE_NEUTRAL;
  }
  if (params.get(PASSWORD_CHANGED_RESULT_PARAM) !== PASSWORD_CHANGED_RESULT_VALUE) {
    return PASSWORD_CHANGED_MODE_NEUTRAL;
  }

  const tsValues = params.getAll(PASSWORD_CHANGED_TS_PARAM);
  if (tsValues.length !== 1 || !/^\d{1,12}$/.test(tsValues[0])) {
    return PASSWORD_CHANGED_MODE_NEUTRAL;
  }

  const issuedAtSec = Number(tsValues[0]);
  const nowSec = Math.floor(Number(nowMs) / 1000);
  if (!Number.isFinite(issuedAtSec) || !Number.isFinite(nowSec)) {
    return PASSWORD_CHANGED_MODE_NEUTRAL;
  }

  const ageSec = nowSec - issuedAtSec;
  if (ageSec > PASSWORD_CHANGED_MAX_AGE_SEC) {
    return PASSWORD_CHANGED_MODE_NEUTRAL;
  }
  if (-ageSec > PASSWORD_CHANGED_MAX_FUTURE_SKEW_SEC) {
    return PASSWORD_CHANGED_MODE_NEUTRAL;
  }

  return PASSWORD_CHANGED_MODE_SUCCESS;
}

/**
 * Drop the whole query (evidence and Auth0 ?iss=) from the current history
 * entry so refresh / Back / revisit render neutral copy.
 */
export function stripPasswordChangedEvidence(
  win = typeof window !== "undefined" ? window : null,
) {
  try {
    const loc = win?.location;
    if (!loc || !loc.search) return;
    win.history.replaceState(
      win.history.state,
      "",
      `${loc.pathname}${loc.hash || ""}`,
    );
  } catch {
    /* ignore — copy already chosen; cleanup is independent */
  }
}
